// Storage для access-токена.
//
// Web — localStorage (silent re-auth между вкладками через BroadcastChannel).
// Для web НЕ храним refresh-token в JS — он в HttpOnly cookie. AccessToken
// нужен только для Authorization header (tRPC client + Socket.IO).
//
// Capacitor — тот же localStorage как быстрый синхронный кэш ПЛЮС зеркало в
// `@capacitor/preferences`. Хранилище WebView очищается вместе с данными
// приложения (`pm clear`, «Очистить данные»), и офлайн-сессия слетала в гостя,
// хотя локальная база с материалами оставалась на месте. Preferences переживает
// очистку WebView, поэтому при старте гидратируем токен обратно.

import { IS_CAPACITOR } from "./platform-detect";

const STORAGE_KEY = "studyhub.accessToken";

interface PreferencesApi {
	get: (opts: { key: string }) => Promise<{ value: string | null }>;
	set: (opts: { key: string; value: string }) => Promise<void>;
	remove: (opts: { key: string }) => Promise<void>;
}

/** Specifier через переменную — иначе Vite import-analysis падает в web-сборке. */
async function loadPreferences(): Promise<PreferencesApi | null> {
	if (!IS_CAPACITOR || typeof window === "undefined") return null;
	const moduleId = "@capacitor/preferences";
	try {
		const mod = (await import(moduleId)) as { Preferences: PreferencesApi };
		return mod.Preferences;
	} catch {
		return null;
	}
}

export function getAccessToken(): string | null {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

export function setAccessToken(token: string): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(STORAGE_KEY, token);
	} catch {
		// ignore — пользователь отключил storage; работаем только из cookie.
	}
	void loadPreferences().then((prefs) => prefs?.set({ key: STORAGE_KEY, value: token }));
}

export function clearAccessToken(): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.removeItem(STORAGE_KEY);
	} catch {
		// ignore
	}
	void loadPreferences().then((prefs) => prefs?.remove({ key: STORAGE_KEY }));
}

/**
 * Восстановить токен из Preferences, если WebView-хранилище пусто.
 * Вызывается на старте мобильного приложения до первых запросов.
 */
export async function hydrateAccessTokenFromNativeStorage(): Promise<void> {
	if (!IS_CAPACITOR || getAccessToken()) return;
	const prefs = await loadPreferences();
	if (!prefs) return;
	try {
		const { value } = await prefs.get({ key: STORAGE_KEY });
		if (value) window.localStorage.setItem(STORAGE_KEY, value);
	} catch {
		// Preferences недоступны — остаёмся без токена, пользователь войдёт заново.
	}
}
